// NAS (tarmoq kirish serverlari) — `xsib-platform` `nas` moduli bilan
// HAQIQIY kontrakt. Manba KODDAN o'qilgan, taxmin qilinmagan:
//   `platform/nas/resource/NasController.java`
//   `platform/nas/dto/{NasEntry,NasPage,NasDraft,NasUpdate,NasSecretChange}.java`
//   `platform/nas/resource/NasExceptionHandler.java`
//
//   GET    /api/nas?page&size   → {rows, total, page, size}
//   GET    /api/nas/{id}        → NasEntry
//   POST   /api/nas             → 201 + NasEntry
//   PUT    /api/nas/{id}        → NasEntry            (SIRSIZ)
//   PUT    /api/nas/{id}/secret → OperationResult
//   DELETE /api/nas/{id}        → OperationResult
//
// Gateway `/api/nas/**` ni platform servisiga uzatadi
// (`xsib-gateway/application.yml`, `services.platform.api-paths`) — shuning
// uchun yo'l `/platform/api/nas` EMAS, oddiy `/api/nas`.
//
// ⚠️ `total` BOR (`hasNext` emas) — `NasPage` javadoc'i buni ataylab
// tushuntiradi: `nas` qo'lda kiritiladigan kichik ro'yxat, `COUNT(*)` bu
// hajmda arzon. Ya'ni `pageResult.syntheticTotal` bu yerda KERAK EMAS va
// jadval haqiqiy sonni ko'rsatadi.
//
// ⚠️⚠️ SIR (`secret`) HECH QACHON O'QILMAYDI. `NasEntry` da bunday maydon
// YO'Q va bu tasodif emas — RADIUS umumiy siri butun autentifikatsiya
// zanjirining asosi. Quyidagi sxema uni QABUL QILMAYDI (zod noma'lum
// maydonlarni tashlab yuboradi), ustiga `assertNoSecret` javob tanasini
// tekshiradi: agar backend kelajakda uni qaytara boshlasa, bu panelda
// jimgina ekranga chiqib ketmasligi kerak.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { uuidSchema } from '@/shared/api/schemas'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

/**
 * ⚠️ `id` — SON (`nas.id` = `serial`), UUID EMAS. Boshqa modullarda
 * `uuidSchema` ishlatilgani uchun bu yerda ham shunday yozish oson xato
 * bo'lardi va zod HAR javobni rad etardi (`auditApi.ts` da aynan shu
 * ushlangan).
 *
 * `type` — bazada `NOT NULL DEFAULT 'other'`, lekin sxema baribir
 * `nullish`: panel bazadagi cheklovga emas, javobga ishonadi.
 */
const nasEntrySchema = z.object({
  id: z.number(),
  nasname: z.string(),
  shortname: z.string(),
  type: z.string().nullish(),
  ports: z.number().nullish(),
  server: z.string().nullish(),
  community: z.string().nullish(),
  description: z.string().nullish(),
  /**
   * Qurilma qaysi joylashuvda turibdi (`site` reyestriga havola).
   *
   * ⚠️ `nullish`: maydon backendda YANGI va mavjud yozuvlarda `null`. Uni
   * majburiy qilish butun ro'yxatni zod xatosi bilan yiqitardi — panel esa
   * bu maydonsiz ham to'liq ishlashi kerak.
   */
  siteId: uuidSchema.nullish(),
})

const nasPageSchema = z.object({
  rows: z.array(nasEntrySchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export interface NasEntry {
  id: number
  nasname: string
  shortname: string
  type: string | null
  ports: number | null
  server: string | null
  community: string | null
  description: string | null
  /** Joylashuv reyestridagi yozuv id'si — `null` = ko'rsatilmagan */
  siteId: string | null
}

/**
 * ⚠️ `ports` ENDI TO'G'RIDAN-TO'G'RI O'QILADI — vaqtinchalik
 * `ports === 0 ? null : ...` o'qishi OLIB TASHLANDI.
 *
 * TARIXI (yana qaytmasligi uchun yozib qoldiriladi). Backend
 * `NasRepository.map` da `rs.wasNull()` `ports` dan KEYIN o'qilgan
 * ustunga (`type`) tegishli bo'lib qolgan edi:
 *
 *     int ports = rs.getInt("ports");
 *     ... rs.getString("type"), rs.wasNull() ? null : ports, ...
 *
 * `type` esa `NOT NULL DEFAULT 'other'`, ya'ni shart hech qachon
 * bajarilmasdi va bazadagi `NULL ports` javobda `0` bo'lib kelardi —
 * panelda "0 port" degan yolg'on ko'rinardi. O'shanda panel `0` ni
 * "sozlanmagan" deb o'qib turgan edi.
 *
 * NEGA U ENDI ZARARLI: backend tuzatildi (`ports` alohida o'zgaruvchiga
 * o'qiladi va `wasNull()` darhol undan keyin tekshiriladi,
 * `NasRowMapperTest` buni qo'riqlaydi). Vaqtinchalik o'qish qolsa,
 * operator ATAYLAB kiritgan `0` panelda "—" bo'lib ko'rinardi va
 * tahrirlashda `null` bo'lib qaytib yuborilardi — ya'ni endi panelning
 * O'ZI ma'lumotni buzadigan tomon bo'lib qolardi.
 */
function toNas(raw: z.infer<typeof nasEntrySchema>): NasEntry {
  return {
    id: raw.id,
    nasname: raw.nasname,
    shortname: raw.shortname,
    type: raw.type ?? null,
    ports: raw.ports ?? null,
    server: raw.server ?? null,
    community: raw.community ?? null,
    description: raw.description ?? null,
    siteId: raw.siteId ?? null,
  }
}

/**
 * Javobda `secret` bormi — bo'lsa BU NOSOZLIK, jimgina o'tkazilmaydi.
 *
 * Zod uni baribir tashlab yuboradi (sxemada bunday maydon yo'q), ya'ni
 * ekranga chiqmaydi. Lekin sirning tarmoqdan o'tayotgani o'z-o'zicha
 * muammo: u brauzer tarmoq panelida, keshda va log'da qoladi. Shuning
 * uchun konsolga ANIQ yozamiz — sinovchi buni ko'radi va backendga
 * xabar qiladi (panel o'zi tuzata olmaydi).
 */
function assertNoSecret(body: unknown): void {
  const rows: unknown[] =
    Array.isArray(body) ? body
    : typeof body === 'object' && body !== null && 'rows' in body && Array.isArray(body.rows) ? body.rows
    : [body]
  for (const row of rows) {
    if (typeof row === 'object' && row !== null && 'secret' in row) {
      console.error(
        '[xsib] XAVFSIZLIK: /api/nas javobida `secret` maydoni bor. ' +
          'Panel uni ko\'rsatmaydi, lekin backend uni umuman qaytarmasligi kerak.',
      )
      return
    }
  }
}

/**
 * Ro'yxat — `DataTable` kutgan shaklda.
 *
 * ⚠️ Backend sahifani 0 dan sanaydi, `TableState.page` esa 1 dan
 * (foydalanuvchi ko'radigan raqam) — boshqa ro'yxatlar bilan bir xil
 * andoza (`auditApi.fetchAuditPage`).
 *
 * ⚠️ QIDIRUV ENDI SERVERDA (`GET /api/nas?q=`) — `nasname`, `shortname`
 * va `description` bo'yicha. Ilgari u umuman yo'q edi va `DataTable`
 * `searchable` emasdi. Mijoz tomonida filtrlash YECHIM EMASDI: ro'yxat
 * sahifalangan, ya'ni filtr faqat JORIY sahifani ko'rardi va operator
 * "bunday qurilma yo'q" degan yolg'on xulosaga kelardi.
 *
 * ⚠️ SARALASH hamon serverda yo'q — backend har doim `shortname` bo'yicha
 * qaytaradi. Shuning uchun ustunlarda `sorter` QO'YILMAGAN: qo'yilsa u
 * faqat joriy sahifani saralab, xuddi shu yolg'onni takrorlardi.
 *
 * ⚠️ Javob FAQAT joriy tashkilotning qurilmalarini o'z ichiga oladi
 * (`platform.nas_tenant` + RLS). `total` ham shunga mos — u boshqa
 * tashkilotlarning qurilmalari sonini oshkor qilmaydi.
 */
export async function fetchNasPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<NasEntry>> {
  const search = state.search.trim()
  const response = await api.get<unknown>('/api/nas', {
    signal,
    // ⚠️ Bo'sh qidiruv UMUMAN yuborilmaydi (`undefined`), bo'sh satr
    // sifatida emas — backend bo'shni "filtrsiz" deb o'qiydi, lekin
    // parametrni umuman yubormaslik so'rov URL'ini ham, keshni ham
    // toza saqlaydi.
    params: { q: search === '' ? undefined : search, page: state.page - 1, size: state.pageSize },
  })
  assertNoSecret(response.data)
  const parsed = nasPageSchema.parse(response.data)
  return { items: parsed.rows.map(toNas), total: parsed.total }
}

export async function fetchNas(id: number): Promise<NasEntry> {
  const response = await api.get<unknown>(`/api/nas/${id}`)
  assertNoSecret(response.data)
  return toNas(nasEntrySchema.parse(response.data))
}

/**
 * Yaratish uchun kirish ma'lumoti — `NasDraft` ning aynan o'zi.
 *
 * `secret` FAQAT shu yerda (yaratishda) yuboriladi. Keyin uni almashtirish
 * alohida yo'l orqali ({@link setNasSecret}) — `NasDraft` javadoc'idagi
 * sabab: oddiy tahrirlash (tavsif, port) sirni QAYTA YUBORISHGA majbur
 * qilmasligi kerak, aks holda panel uni saqlab yurishga majbur bo'lardi.
 */
export interface NasDraft {
  nasname: string
  shortname: string
  type?: string | null
  ports?: number | null
  secret: string
  server?: string | null
  community?: string | null
  description?: string | null
  /** Joylashuv (`site`) — ixtiyoriy, lekin usiz "bu router qayerda?" degan savol javobsiz qoladi */
  siteId?: string | null
}

/** Tahrirlash — SIRSIZ (`NasUpdate`) */
export type NasUpdate = Omit<NasDraft, 'secret'>

export async function createNas(draft: NasDraft): Promise<NasEntry> {
  const response = await api.post<unknown>('/api/nas', draft)
  assertNoSecret(response.data)
  return toNas(nasEntrySchema.parse(response.data))
}

export async function updateNas(id: number, draft: NasUpdate): Promise<NasEntry> {
  const response = await api.put<unknown>(`/api/nas/${id}`, draft)
  assertNoSecret(response.data)
  return toNas(nasEntrySchema.parse(response.data))
}

/**
 * Sirni almashtirish.
 *
 * ⚠️ `OperationResult` qaytaradi, `NasEntry` emas — `NasController.setSecret`
 * javadoc'i. Bu yerda `PARTIAL` amalda kutilmaydi (faqat baza yozuvi, CoA
 * yo'q), lekin natija baribir `OperationResultAlert` orqali ko'rsatiladi:
 * shakl kontraktda shunday va uni "har doim OK" deb soddalashtirish
 * kelajakdagi `PARTIAL` ni jimgina yutib yuborardi.
 */
export async function setNasSecret(id: number, secret: string): Promise<OperationResult> {
  const response = await api.put<unknown>(`/api/nas/${id}/secret`, { secret })
  return operationResultSchema.parse(response.data)
}

export async function deleteNas(id: number): Promise<OperationResult> {
  const response = await api.delete<unknown>(`/api/nas/${id}`)
  return operationResultSchema.parse(response.data)
}

// --- Tiriklik belgisi (GET /api/nas/health) ----------------------------------
//
// ⚠️ RO'YXATDAN ALOHIDA SO'ROV, va bu ataylab. Sozlama (`NasEntry`) kamdan-kam
// o'zgaradi, holat esa har daqiqada. Bittasiga birlashtirilsa panel holatni
// yangilash uchun butun ro'yxatni — qidiruv, sahifalash va `COUNT(*)` bilan
// birga — qayta so'rashga majbur bo'lardi, va tahrirlash oynasi ochiq turganda
// ma'lumot oyoq ostidan o'zgarardi.
//
// ⚠️ JAVOB SAHIFALANMAYDI: backend tashkilotning BARCHA qurilmalarini
// qaytaradi (`NasHealthReport` javadoc'i). Sabab — sahifalangan belgi
// "hammasi joyida" degan yolg'on xulosaga olib kelardi: ikkinchi sahifadagi
// o'lik router ko'rinmasdi.
//
// ⚠️ QURILMAGA HECH QANDAY SO'ROV YUBORILMAYDI. Hamma qiymat FreeRADIUS
// accounting yozuvlaridan (`radacct`) o'qiladi.

/**
 * Holat qiymatlari — backenddagi `NasHealth.State` bilan AYNAN bir xil.
 *
 * ⚠️ Ro'yxat `z.enum` bilan QOTIRILGAN, `z.string()` emas: backend yangi
 * holat qo'shsa panel uni jimgina "noma'lum" qilib chizib ketmasin, zod
 * xatosi bilan darhol ko'rinsin.
 */
const nasHealthStateSchema = z.enum(['ONLINE', 'SILENT', 'IDLE', 'NO_DATA', 'UNMATCHED'])

export type NasHealthState = z.infer<typeof nasHealthStateSchema>

const nasHealthSchema = z.object({
  nasId: z.number(),
  nasname: z.string(),
  shortname: z.string(),
  state: nasHealthStateSchema,
  /** ISO-8601 UTC; `null` — oyna ichida birorta accounting yozuvi yo'q */
  lastContact: z.string().nullish(),
  /**
   * Oxirgi aloqadan beri o'tgan soniya.
   *
   * ⚠️ SERVERDA hisoblangan va shunday QOLADI. Uni `Date.now()` bilan
   * qayta hisoblash operator brauzerining soati noto'g'ri bo'lsa "14 daqiqa
   * oldin" o'rniga butunlay boshqa raqam berardi — va aynan shu raqamga
   * qarab "router o'chganmi?" degan qaror qabul qilinadi.
   */
  silentSeconds: z.number().nullish(),
  thresholdSeconds: z.number(),
  interimInterval: z.number().nullish(),
  activeSessions: z.number(),
  lastSessionStart: z.string().nullish(),
  reboots: z.number(),
  lastReboot: z.string().nullish(),
})

const nasHealthReportSchema = z.object({
  rows: z.array(nasHealthSchema),
  generatedAt: z.string(),
  windowSeconds: z.number(),
})

export interface NasHealth {
  nasId: number
  nasname: string
  shortname: string
  state: NasHealthState
  lastContact: string | null
  silentSeconds: number | null
  thresholdSeconds: number
  interimInterval: number | null
  activeSessions: number
  lastSessionStart: string | null
  reboots: number
  lastReboot: string | null
}

export interface NasHealthReport {
  rows: NasHealth[]
  generatedAt: string
  /** Accounting tarixi qancha orqaga o'qilgani — UI buni AYTISHI kerak */
  windowSeconds: number
}

export async function fetchNasHealth(signal: AbortSignal): Promise<NasHealthReport> {
  const response = await api.get<unknown>('/api/nas/health', { signal })
  const parsed = nasHealthReportSchema.parse(response.data)
  return {
    rows: parsed.rows.map((row) => ({
      nasId: row.nasId,
      nasname: row.nasname,
      shortname: row.shortname,
      state: row.state,
      lastContact: row.lastContact ?? null,
      silentSeconds: row.silentSeconds ?? null,
      thresholdSeconds: row.thresholdSeconds,
      interimInterval: row.interimInterval ?? null,
      activeSessions: row.activeSessions,
      lastSessionStart: row.lastSessionStart ?? null,
      reboots: row.reboots,
      lastReboot: row.lastReboot ?? null,
    })),
    generatedAt: parsed.generatedAt,
    windowSeconds: parsed.windowSeconds,
  }
}

// --- Mijoz tomonidagi tekshiruv ---------------------------------------------

/**
 * Backenddagi eng past chegara (`NasDraft`: "secret kamida 8 belgi").
 *
 * Mijoz tomonda TAKRORLANADI, chunki sir formasi yuborilganda server rad
 * etsa operator yozganini qaytadan kiritishga majbur bo'lardi. Server
 * tekshiruvi baribir hal qiluvchi bo'lib qoladi.
 */
export const NAS_SECRET_MIN_LENGTH = 8

/**
 * `nasname` — IPv4, IPv4/CIDR yoki host nomi.
 *
 * ⚠️ NEGA MIJOZDA TEKSHIRILADI: bu maydon FreeRADIUS uchun qurilmani
 * TOPISH kaliti. Xato yozilgan manzil server tomonidan qabul qilinadi
 * (baza `text`, cheklov yo'q) va nosozlik faqat keyin — abonent ulana
 * olmaganda — bilinadi. Ya'ni bu yerda tekshirish server tekshiruvini
 * takrorlash emas, u umuman mavjud bo'lmagan joyni yopadi.
 *
 * Naqsh ATAYLAB keng: FreeRADIUS host nomini ham, tarmoq oralig'ini ham
 * qabul qiladi. Faqat aniq nosozlik rad etiladi — bo'sh joy, ikkilangan
 * nuqta, noto'g'ri oktet.
 */
const IPV4_OCTET = '(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)'
const IPV4 = `${IPV4_OCTET}(\\.${IPV4_OCTET}){3}`
const IPV4_CIDR = new RegExp(`^${IPV4}(/(3[0-2]|[12]?\\d))?$`)
const HOSTNAME = /^(?=.{1,253}$)[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

/**
 * Oxirgi bo'g'ini FAQAT RAQAMDAN iborat nom — bu host nomi EMAS.
 *
 * ⚠️ BU QATOR O'LCHANGAN TESHIKNI YOPADI (Staging, 2026-08-16).
 * `999.1.1.1` maydonga kiritildi va HECH QANDAY xato chiqmadi: u
 * `IPV4_CIDR` dan o'tmaydi (`999` — oktet emas), lekin `HOSTNAME` dan
 * BEMALOL o'tadi, chunki raqam ham to'g'ri belgi. Ya'ni tekshiruv bor
 * edi, va aynan eng ko'p uchraydigan xato — IP dagi terish xatosi —
 * undan jimgina o'tib ketardi. Uchta boshqa yaroqsiz qiymat
 * (`abc def`, `10.10..2`, `not a host!!`) o'sha o'lchovda TO'G'RI rad
 * etilgan edi, ya'ni nuqson faqat shu shaklda ko'rinardi.
 *
 * Qoida o'zboshimchalik emas: RFC 1123 bo'yicha yuqori darajali domen
 * to'liq raqamdan iborat bo'lolmaydi — aynan shu ikkilanishni yo'q
 * qilish uchun. Demak `999.1.1.1` ni rad etish host nomlarining
 * BIRORTASINI ham yo'qotmaydi, faqat IP terish xatosini ushlaydi.
 *
 * ⚠️ Nuqtasiz sof raqam (`12345`) ham shu qoidaga tushadi va bu to'g'ri:
 * u ham manzil emas.
 */
const NUMERIC_LAST_LABEL = /(^|\.)\d+$/

export function isValidNasName(value: string): boolean {
  const trimmed = value.trim()
  if (trimmed === '') return false
  if (IPV4_CIDR.test(trimmed)) return true
  // ⚠️ TARTIB MUHIM: to'g'ri IPv4 yuqorida allaqachon qabul qilingan,
  // ya'ni bu shart faqat "IP ga o'xshaydi, lekin IP emas" qolgan
  // qiymatlarni ushlaydi.
  if (NUMERIC_LAST_LABEL.test(trimmed)) return false
  return HOSTNAME.test(trimmed)
}

/** `nasname` IP (yoki tarmoq oralig'i) ko'rinishidami — kartochkada belgi uchun */
export function looksLikeIp(value: string): boolean {
  return IPV4_CIDR.test(value.trim())
}

// --- Boshqaruv kanalini FAOL tekshirish -------------------------------------
//
// Manba: `session/coa/resource/SessionControlResource#probeNas`,
// `session/coa/service/NasProbe`.
//
// ⚠️ NEGA MAVJUD "tiriklik" BELGISI YETARLI EMAS. U accounting
// paketlariga qaraydi — ya'ni QURILMADAN BIZGA kelgan trafikka. CoA esa
// teskari yo'nalishda ketadi va butunlay boshqa sabab bilan buzilishi
// mumkin. 2026-08-10 da aynan shunday bo'ldi: auth va accounting
// benuqson ishlab turgan paytda CoA umuman o'tmasdi (umumiy sir mos
// kelmagan), panel esa qurilmani "tirik" deb ko'rsatardi — va bu
// TO'G'RI edi, shunchaki boshqa savolga javob berardi.

export const PROBE_STATUSES = ['ALIVE', 'UNREACHABLE', 'RELAY_DOWN'] as const
export type ProbeStatus = (typeof PROBE_STATUSES)[number]

export interface ProbeResult {
  status: ProbeStatus
  /** Operatorga ko'rsatiladigan izoh — nima qilish kerakligini aytadi */
  detail: string
}

const probeResultSchema = z.object({ status: z.enum(PROBE_STATUSES) })

/**
 * ⚠️ Bu amal MAVJUD BO'LMAGAN sessiyaga Disconnect yuboradi, ya'ni
 * hech qanday abonentni uza olmaydi. Shuning uchun u `nas.read` bilan
 * ochiq — diagnostikani boshqaruv huquqi ortiga yashirish
 * qo'llab-quvvatlash xodimini eng kerakli vositadan mahrum qilardi.
 */
export async function probeNas(nasIp: string): Promise<ProbeStatus> {
  const response = await api.post<unknown>('/api/sessions/nas-probe', undefined, {
    params: { nasIp },
  })
  return probeResultSchema.parse(response.data).status
}
